import React, { useState, useEffect, useMemo } from 'react';
import { Modal, ModalBody } from 'reactstrap';
import './ChangeStaffModal.css';
import type { StaffOption } from 'types/models';

interface ChangeStaffModalProps {
  isOpen: boolean;
  toggle: () => void;
  onSelectStaff: (staff: StaffOption) => void;
  selectedStaffId?: string | number;
  staffList?: StaffOption[];
}

const ChangeStaffModal = ({
  isOpen,
  toggle,
  onSelectStaff,
  selectedStaffId = '',
  staffList = [],
}: ChangeStaffModalProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const filteredStaff = useMemo(() => {
    const filtered = staffList.filter((staff) =>
      staff.name?.toLowerCase().includes(searchTerm?.toLowerCase())
    );
    return filtered;
  }, [searchTerm, staffList]);

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleSelectStaff = (staff: StaffOption) => {
    onSelectStaff(staff);
  };

  const displayStaff = filteredStaff.length > 0 ? filteredStaff : staffList;

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      className='change-staff-modal'
      contentClassName='change-staff-modal-content'
      fullscreen={true}
    >
      <ModalBody className='p-0'>
        <div className='change-staff-container'>
          <div className='change-staff-header'>
            <button className='close-button' onClick={toggle}>
              <svg
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M18 6L6 18'
                  stroke='#1D2129'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <path
                  d='M6 6L18 18'
                  stroke='#1D2129'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </button>
            <h2 className='header-title'>Change Staff</h2>
          </div>

          <div className='search-container'>
            <div className='search-input-wrapper'>
              <svg
                className='search-icon'
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z'
                  stroke='#64748B'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <path
                  d='M21 21L16.65 16.65'
                  stroke='#64748B'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
              <input
                type='text'
                placeholder='Staff name'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className='search-input'
              />
              {searchTerm && (
                <button className='clear-button' onClick={handleClearSearch}>
                  <svg
                    width='24'
                    height='24'
                    viewBox='0 0 24 24'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      d='M18 6L6 18'
                      stroke='#64748B'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                    <path
                      d='M6 6L18 18'
                      stroke='#64748B'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>

          <div className='staff-grid'>
            {displayStaff.map((staff) => (
              <div
                key={staff.id}
                className={`staff-item ${selectedStaffId === staff.id ? 'selected' : ''
                  }`}
                onClick={() => handleSelectStaff(staff)}
              >
                <div className='avatar-container'>
                  <img
                    src={staff.avatar}
                    alt={staff.name}
                    className='staff-avatar'
                  />
                  {selectedStaffId === staff.id && (
                    <div className='check-mark'>
                      <svg
                        width='31'
                        height='31'
                        viewBox='0 0 31 31'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <path
                          d='M12.5826 23.4146C12.1365 23.4146 11.7127 23.2361 11.4005 22.9239L5.08815 16.6117C4.44132 15.9648 4.44132 14.8942 5.08815 14.2474C5.73499 13.6005 6.80561 13.6005 7.45245 14.2474L12.5826 19.3774L24.0472 7.91286C24.694 7.26603 25.7647 7.26603 26.4115 7.91286C27.0583 8.55969 27.0583 9.63032 26.4115 10.2772L13.7648 22.9239C13.4525 23.2361 13.0287 23.4146 12.5826 23.4146Z'
                          fill='#0085E7'
                        />
                      </svg>
                    </div>
                  )}
                </div>
                <div className='staff-name'>{staff.name}</div>
              </div>
            ))}
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default ChangeStaffModal;
